import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { videoSource, youTubeThumb } from "@/lib/youtube";
import { resolveMedia } from "@/lib/api";

/*
  A single Video Gallery tile: thumbnail + title + one-line caption.
  Clicking opens the shared player modal. YouTube shows its auto-thumbnail,
  Vimeo uses a manual poster, MP4 plays a silent in-view preview frame.
*/
export const GalleryTile = ({ item, onOpen, delay = 0 }) => {
  const ref = useRef(null);
  const vidRef = useRef(null);
  const [show, setShow] = useState(false);
  const src = videoSource(item.video_url);
  const poster = resolveMedia(item.poster);
  const ytThumb = src.kind === "youtube" ? youTubeThumb(item.video_url) : null;
  const thumb = poster || ytThumb;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShow(true);
          io.unobserve(e.target);
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // MP4 preview: play only while visible
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.4 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <button
      ref={ref}
      onClick={(e) => onOpen(item, e)}
      className="group relative block aspect-video w-full overflow-hidden rounded-2xl bg-[var(--charcoal)] text-left"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "none" : "translateY(28px)",
        transition: "opacity .9s var(--ease), transform .9s var(--ease)",
        transitionDelay: `${delay}ms`,
      }}
      data-testid={`gallery-tile-${item.id}`}
      aria-label={`Play ${item.title}`}
    >
      {src.kind === "mp4" && !poster ? (
        <video
          ref={vidRef}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          src={resolveMedia(src.src)}
          poster={poster || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
      ) : thumb ? (
        <img
          src={thumb}
          alt={item.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--charcoal)]" />
      )}

      {/* gradient + play */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#141210]/85 via-[#141210]/10 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--mint)] text-[var(--charcoal)] opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:scale-105">
          <Play size={20} fill="currentColor" />
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-5">
        <h3 className="text-lg font-semibold tracking-tight text-white">{item.title}</h3>
        {item.caption && <p className="mt-1 text-[13px] leading-snug text-white/70">{item.caption}</p>}
      </div>
    </button>
  );
};
